
import React, { useState, useEffect } from 'react';

import HeaderMagraGroup from 'img/header-mafra-group.svg';
import HeaderLogoCCare from 'img/customer-care-header-logo.svg';

import { Column, Row } from 'components/Layout/Grid';
import { Visibility } from 'components/Visibility';
import { Button } from 'components/Button';
import Text from 'components/Text';

import TicketServices from 'services/ticket.service';

import { ITickets } from 'models/Ticket';
import {
  EnumErrorCodeRequest,
  ErrorState
} from './interface';

import { useQuery } from 'hooks/useQuery';
import MisplacementProcedureService from 'services/misplacementProcedure.service';
import { IOrderItem } from 'models/Order';

import keycloak from 'utils/keycloak';
import { currencyOutputNumber } from 'utils/CurrencyOutput';
import { formatDocument } from 'utils/Document';

import { PageSuccessByEmail } from 'components/RequestReturnPageByEmail/PageSuccessByEmail';
import { PageWarningByEmail } from 'components/RequestReturnPageByEmail/PageWarningByEmail';
import Loading from 'components/Loading';

import {
  Container,
  Content,
  WrapperItems
} from './styles';

export const ProcessCompletionLoss = () => {

  const [errorRequest, setErrorRequest] = useState<ErrorState>({
    isError: false,
    field: "",
    message: "",
  });

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);

  const [dateProtocol, setDateProtocol] = useState<ITickets | undefined>(undefined);
  const [isValidProcess, setIsValidProcess] = useState<boolean>(false);

  const protocolIdParams: string | null = useQuery().get('protocolId') ?? "";

  const _ticketService = new TicketServices();
  const _misplacementProcedureService = new MisplacementProcedureService();

  useEffect(() => {
    getProtocol();
  }, []);

  const getProtocol = () => {
    setIsLoadingPage(true);

    if (!protocolIdParams) {
      addErrorRequest(EnumErrorCodeRequest.PROTOCOL_NOT_FOUND, "Protocolo não encontrado em nosso sistema.");
      setIsLoadingPage(false);

      return false;
    }

    _ticketService
      .get(undefined, undefined, undefined, { id: protocolIdParams || "" })
      .then(({ error, data }) => {
        if (data?.length > 0 && !error) {
          data.map((ticket: ITickets) => {
            const documentNumber = ticket?.company?.documentNumber ?? "";
            ticket.company = { ...ticket.company, documentNumber: formatDocument(documentNumber) };
          });
          setDateProtocol(data[0]);
        } else {
          addErrorRequest(EnumErrorCodeRequest.PROTOCOL_NOT_FOUND, "Protocolo não encontrado em nosso sistema.");
        }
      }).catch(() => {
        addErrorRequest(EnumErrorCodeRequest.GENERIC_ERROR, "");
      }).finally(() => {
        setIsLoadingPage(false);
      });
  };

  const sendValidProcess = async () => {
    setIsLoadingBtn(true);

    const dataObj = {
      ticketId: protocolIdParams,
      userName: keycloak.profile?.username
    };

    await _misplacementProcedureService.putLossFlowApproval(dataObj)
      .then((response) => {
        if (response.status === 200)
          setIsSuccess(true);

        if (response.status !== 200) {
          addErrorRequest(EnumErrorCodeRequest.GENERIC_ERROR, response?.data?.data);
          setIsSuccess(false);
        }
      })
      .catch(() => {
        setIsSuccess(false);
        addErrorRequest(EnumErrorCodeRequest.GENERIC_ERROR, "Ocorreu um erro na requisição");
      })
      .finally(() => setIsLoadingBtn(false));
  };

  const addErrorRequest = (field: string, msg: string) => {
    setErrorRequest({
      isError: true,
      field: field,
      message: msg || "Ocorreu um erro."
    });

    return true;
  };

  const conditionShowScheduleSuccess = (isSuccess && !errorRequest.isError);
  const conditionShowPage = (!isSuccess && !errorRequest.isError && !isLoadingPage);
  const isError = errorRequest.isError;

  return (
    <Container>
      <div className="header-logo-box mr-2">
        <img
          className="mr-3"
          src={HeaderMagraGroup}
          alt=""
        />
        <img
          src={HeaderLogoCCare}
          alt=""
          width={110}
          height={30}
        />
      </div>

      <Content>

        <Visibility visible={isLoadingPage}>
          <Row className="align-center justify-center mt-10">
            <Loading onlySpinner size={50} />
          </Row>
        </Visibility>

        <Visibility visible={isError}>
          <PageWarningByEmail
            title={errorRequest.message}
          />
        </Visibility>

        <Visibility visible={conditionShowScheduleSuccess}>
          <PageSuccessByEmail title="Processo validado" />
        </Visibility>

        <Visibility visible={conditionShowPage}>
          <Text className="color-primary font-bold" size={24}>
            Finalização do processo de extravio / furto
          </Text>

          <Column className="mt-5 grid-gap-1">
            <Text className="color-blue-gray" weight="bold" size={16}>
              Protocolo: <strong>{dateProtocol?.protocolNumber}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Cliente: <strong>{dateProtocol?.company?.companyName}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              CNPJ: <strong>{dateProtocol?.company?.documentNumber}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Pedido: <strong>{dateProtocol?.order?.protheusOrderSale}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Nota Fiscal: <strong>{`${dateProtocol?.order?.invoiceNumber || ''}/${dateProtocol?.order?.invoiceSerialNumber || ''}`}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Transportadora: <strong>{dateProtocol?.order?.shippingCompanyName}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Empresa: <strong>{dateProtocol?.order?.enterpriseName}</strong>
            </Text>

            <Text className="color-blue-gray" weight="bold" size={16}>
              Valor da Reclamação: <strong>{currencyOutputNumber(dateProtocol?.reclamationPrice || 0)}</strong>
            </Text>
          </Column>

          <Column className="mt-5">
            <Text className="color-blue-gray" weight="bold" size={18}>
              Itens do Pedido
            </Text>

            <WrapperItems>
              {dateProtocol?.order?.items.map((item: IOrderItem) => {
                return (
                  <Column className="mt-3 grid-gap-1" key={item?.code}>
                    <Text className="color-blue-gray" weight="bold" size={16}>
                      Produto: <strong>{item?.name}</strong>
                    </Text>

                    <Text className="color-blue-gray" weight="bold" size={16}>
                      Lote: <strong>{item?.lot}</strong>
                    </Text>
                  </Column>
                );
              })}

            </WrapperItems>
          </Column>

          <Row className='grid-gap-1 align-center mt-6'>
            <input
              id="accept_valid_process"
              className="mr-2 check-multiselect"
              type="checkbox"
              defaultChecked={isValidProcess}
              onClick={() => setIsValidProcess(!isValidProcess)}
            />

            <label className="color-blue-gray cursor-pointer" htmlFor="accept_valid_process">
              Estou ciente deste processo de extravio/furto
            </label>
          </Row>

          <Button
            className="outline"
            width={160}
            isRequesting={isLoadingBtn}
            disabled={!isValidProcess}
            spinnerColor="#0E4C54"
            style={{
              margin: "30px auto 0 auto"
            }}
            onClick={() => sendValidProcess()}
          >
            <Text className="color-primary font-bold px-1">Validar Processo</Text>
          </Button>

        </Visibility>
      </Content>
    </Container >
  );
};
